import React from 'react';
const IndustriesPage = React.lazy(() => import('./pages/IndustriesPage.jsx').then(() => ({ default: window.IndustriesPage })));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage.jsx').then(() => ({ default: window.CategoryPage })));
const ProductsPage = React.lazy(() => import('./pages/ProductsPage.jsx').then(() => ({ default: window.ProductsPage })));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage.jsx').then(() => ({ default: window.ProductDetailPage })));
const AboutPage = React.lazy(() => import('./pages/AboutPage.jsx').then(() => ({ default: window.AboutPage })));
const BlogsPage = React.lazy(() => import('./pages/BlogsPage.jsx').then(() => ({ default: window.BlogsPage })));
const CareersPage = React.lazy(() => import('./pages/CareersPage.jsx').then(() => ({ default: window.CareersPage })));
const ContactPage = React.lazy(() => import('./pages/ContactPage.jsx').then(() => ({ default: window.ContactPage })));
const SupportPage = React.lazy(() => import('./pages/SupportPage.jsx').then(() => ({ default: window.SupportPage })));
const LoginPage = React.lazy(() => import('./pages/LoginPage.jsx').then(() => ({ default: window.LoginPage })));
const IndiaPage = React.lazy(() => import('./pages/IndiaPage.jsx').then(() => ({ default: window.IndiaPage })));
const AdminLayout = React.lazy(() => import('./admin/AdminLayout.jsx').then(() => ({ default: window.AdminLayout })));
const AdminLogin = React.lazy(() => import('./admin/AdminLogin.jsx').then(() => ({ default: window.AdminLogin })));

window.App = function App() {
  const [activeTab, setActiveTab] = React.useState('home');
  const [quoteModalOpen, setQuoteModalOpen] = React.useState(false);
  const [adminUser, setAdminUser] = React.useState(null);
  const [checkingAuth, setCheckingAuth] = React.useState(true);
  const [categoriesList, setCategoriesList] = React.useState(['All', 'Railways', 'Industrial Automation', 'Aerospace', 'Automotive', 'Custom CAD & Fixtures']);
  const [selectedProductCategory, setSelectedProductCategory] = React.useState('All');
  const [selectedProductSlug, setSelectedProductSlug] = React.useState('railway-brake-valve-test-rig');
  const [selectedIndustrySlug, setSelectedIndustrySlug] = React.useState('railway');
  const [selectedSolutionSlug, setSelectedSolutionSlug] = React.useState('railway-testing');
  const [selectedCategoryData, setSelectedCategoryData] = React.useState(null);

  const parseUrl = () => {
    // Get pathname segments
    const rawPath = window.location.pathname;
    const segments = rawPath.split('/').filter(Boolean);
    const knownPages = ['home', 'industries', 'products', 'product-detail', 'category', 'about', 'blogs', 'careers', 'india', 'downloads', 'contact', 'support', 'admin'];
    
    let initialTab = 'home';
    
    // Strictly route only root-level page segments
    if (segments.length > 0) {
      const firstSegment = segments[0];
      if (knownPages.includes(firstSegment)) {
        initialTab = firstSegment;
      }
    }

    // 2. Hash check (admin fallback)
    const hash = window.location.hash.replace('#', '');
    if (hash === 'admin') {
      initialTab = 'admin';
    }
    
    // 3. Query params check
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const slugParam = params.get('slug');
    const catParam = params.get('cat');
    
    if (tabParam) initialTab = tabParam;
    
    setActiveTab(initialTab);
    if (slugParam) {
      setSelectedProductSlug(slugParam);
      setSelectedIndustrySlug(slugParam);
      setSelectedSolutionSlug(slugParam);
    }
    if (catParam) setSelectedProductCategory(catParam);
  };

  // Check URL params and hash on load
  React.useEffect(() => {
    parseUrl();
    
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'admin') {
        setActiveTab('admin');
      }
    };
    
    const handlePopState = (event) => {
      const state = event.state;
      if (state && state.activeTab) {
        setActiveTab(state.activeTab);
        if (state.selectedProductSlug) setSelectedProductSlug(state.selectedProductSlug);
        if (state.selectedProductCategory) setSelectedProductCategory(state.selectedProductCategory);
      } else {
        parseUrl();
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handlePopState);
    checkSession();
    fetchCategories();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Synchronize state changes to clean URLs
  React.useEffect(() => {
    if (checkingAuth) return; // Wait for initial load check
    
    // Sync URL strictly relative to root path since app is hosted at domain root
    let targetPath = activeTab === 'home' ? '/' : `/${activeTab}`;
    if (activeTab === 'admin') {
      targetPath = '/#admin';
    }
    
    const params = new URLSearchParams();
    if (activeTab === 'product-detail' && selectedProductSlug) {
      params.set('slug', selectedProductSlug);
    }
    if (activeTab === 'solutions' && selectedSolutionSlug) {
      params.set('slug', selectedSolutionSlug);
    }
    if (activeTab === 'industries' && selectedIndustrySlug) {
      params.set('slug', selectedIndustrySlug);
    }
    if (activeTab === 'products' && selectedProductCategory && selectedProductCategory !== 'All') {
      params.set('cat', selectedProductCategory);
    }
    
    const queryString = params.toString();
    const fullUrl = targetPath + (queryString ? `?${queryString}` : '');
    
    // Only push state if url changed to avoid loops
    const currentFull = window.location.pathname + window.location.search + window.location.hash;
    if (currentFull !== fullUrl) {
      window.history.pushState({ activeTab, selectedProductSlug, selectedProductCategory }, '', fullUrl);
    }
  }, [activeTab, selectedProductSlug, selectedProductCategory, selectedSolutionSlug, selectedIndustrySlug, checkingAuth]);

  const fetchCategories = async () => {
    try {
      const res = await window.apiClient.get('categories.php');
      if (res.success && res.data && res.data.length > 0) {
        const catNames = res.data.map(c => c.name);
        setCategoriesList(['All', ...catNames]);
      }
    } catch (e) {
      console.warn('App categories fetch fallback:', e);
    }
  };

  const checkSession = async () => {
    try {
      const res = await window.apiClient.get('auth.php?action=check');
      if (res.success && res.authenticated) {
        setAdminUser(res.user);
      }
    } catch (e) {
      console.warn('Session check warning:', e);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Reset scroll position when switching tabs
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab, selectedProductSlug, selectedIndustrySlug, selectedSolutionSlug]);

  // Render Admin View
  if (activeTab === 'admin') {
    if (adminUser) {
      return (
        <React.Suspense fallback={
          <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-900 text-white">
            <div className="w-12 h-12 rounded-full border-4 border-slate-700 border-t-orange-500 animate-spin"></div>
            <p className="font-mono text-[10px] text-slate-400 uppercase tracking-widest animate-pulse">Initializing Portal...</p>
          </div>
        }>
          <AdminLayout 
            user={adminUser} 
            onLogout={async () => {
              try { await window.apiClient.get('auth.php?action=logout'); } catch (e) {}
              setAdminUser(null);
              setActiveTab('home');
              window.location.hash = '';
            }} 
          />
        </React.Suspense>
      );
    } else {
      return (
        <React.Suspense fallback={
          <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-900 text-white">
            <div className="w-12 h-12 rounded-full border-4 border-slate-700 border-t-orange-500 animate-spin"></div>
            <p className="font-mono text-[10px] text-slate-400 uppercase tracking-widest animate-pulse">Loading Login...</p>
          </div>
        }>
          <AdminLogin 
            onLoginSuccess={(user) => {
              setAdminUser(user);
            }} 
          />
        </React.Suspense>
      );
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 font-sans antialiased selection:bg-secondary selection:text-white">
      
      {/* Sticky Header with 9 Top-Level Navigation Nodes */}
      <window.Header 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        openQuoteModal={() => setQuoteModalOpen(true)} 
        categoriesList={categoriesList}
        selectedProductCategory={selectedProductCategory}
        setSelectedProductCategory={setSelectedProductCategory}
        setSelectedIndustrySlug={setSelectedIndustrySlug}
      />

      {/* Main Content View Switcher */}
      <main className="flex-grow">
        <React.Suspense fallback={
          <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 bg-slate-50">
            <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-secondary animate-spin"></div>
            <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest animate-pulse">Loading Page...</p>
          </div>
        }>
          {activeTab === 'home' && (
            <window.HomePage 
              setActiveTab={setActiveTab} 
              openQuoteModal={() => setQuoteModalOpen(true)} 
              setSelectedIndustrySlug={setSelectedIndustrySlug}
              setSelectedProductSlug={setSelectedProductSlug}
              setSelectedProductCategory={setSelectedProductCategory}
            />
          )}

          {activeTab === 'industries' && (
            <IndustriesPage 
              selectedIndustrySlug={selectedIndustrySlug}
              setActiveTab={setActiveTab}
              openQuoteModal={() => setQuoteModalOpen(true)}
              setSelectedProductSlug={setSelectedProductSlug}
              setSelectedProductCategory={setSelectedProductCategory}
              setSelectedCategoryData={setSelectedCategoryData}
            />
          )}

          {activeTab === 'category' && (
            <CategoryPage 
              categoryData={selectedCategoryData}
              setActiveTab={setActiveTab}
              setSelectedProductSlug={setSelectedProductSlug}
              openQuoteModal={() => setQuoteModalOpen(true)}
            />
          )}

          {activeTab === 'products' && (
            <ProductsPage 
              openQuoteModal={() => setQuoteModalOpen(true)} 
              selectedCategory={selectedProductCategory}
              setSelectedCategory={setSelectedProductCategory}
              categoriesList={categoriesList}
              setActiveTab={setActiveTab}
              setSelectedProductSlug={setSelectedProductSlug}
            />
          )}

          {activeTab === 'product-detail' && (
            <ProductDetailPage 
              productSlug={selectedProductSlug}
              setActiveTab={setActiveTab}
              openQuoteModal={() => setQuoteModalOpen(true)}
              setSelectedProductSlug={setSelectedProductSlug}
            />
          )}


          {activeTab === 'about' && (
            <AboutPage 
              setActiveTab={setActiveTab} 
              openQuoteModal={() => setQuoteModalOpen(true)} 
            />
          )}

          {activeTab === 'blogs' && (
            <BlogsPage />
          )}


          {activeTab === 'careers' && (
            <CareersPage setActiveTab={setActiveTab} />
          )}

          {activeTab === 'contact' && (
            <ContactPage 
              openQuoteModal={() => setQuoteModalOpen(true)} 
            />
          )}

          {activeTab === 'support' && (
            <SupportPage />
          )}

          {activeTab === 'login' && (
            <LoginPage setActiveTab={setActiveTab} />
          )}

          {activeTab === 'india' && (
            <IndiaPage setActiveTab={setActiveTab} />
          )}
        </React.Suspense>
      </main>

      {/* Global Footer */}
      <window.Footer 
        setActiveTab={setActiveTab} 
        openQuoteModal={() => setQuoteModalOpen(true)} 
        setSelectedIndustrySlug={setSelectedIndustrySlug}
        setSelectedProductCategory={setSelectedProductCategory}
      />

      {/* Engineering RFQ / Consultation Modal */}
      <window.QuoteModal 
        isOpen={quoteModalOpen} 
        onClose={() => setQuoteModalOpen(false)} 
      />

    </div>
  );
};
